import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { X, Loader2, Send } from "lucide-react";

export default function CastingInquiryModal({ open, target, onClose }) {
  const [form, setForm] = useState({
    role: "", project_name: "", when_needed: "", requirements: "",
    contact_name: "", contact_email: "", contact_phone: "", company: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (open) {
      setSent(false);
      setForm({ role: "", project_name: "", when_needed: "", requirements: "", contact_name: "", contact_email: "", contact_phone: "", company: "", message: "" });
    }
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (!form.role || !form.requirements || !form.contact_name || !form.contact_email) {
      toast.error("Please fill role, requirements, name and email");
      return;
    }
    setBusy(true);
    try {
      await api.post("/casting-inquiry", {
        target_username: target?.username,
        video_id: target?.video_id || null,
        ...form,
      });
      setSent(true);
      toast.success("Inquiry sent — the NextScene team will follow up.");
    } catch (err) {
      toast.error(errMsg(err, "Could not send inquiry"));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="casting-inquiry-modal" className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4">
      <div className="w-full sm:max-w-lg bg-[#0A0605] border border-[#DC2626]/30 rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[92vh] flex flex-col shadow-2xl shadow-[#DC2626]/20">
        <div className="flex items-center justify-between p-4 border-b border-zinc-900 sticky top-0 bg-[#180D0D]">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#DC2626]">Casting inquiry</p>
            <h2 className="font-display text-lg font-semibold text-white mt-0.5">Cast @{target?.username}</h2>
          </div>
          <button data-testid="close-inquiry-modal" onClick={onClose} className="w-9 h-9 rounded-full bg-zinc-900 border border-zinc-800 text-white flex items-center justify-center">
            <X size={16}/>
          </button>
        </div>

        {sent ? (
          <div data-testid="inquiry-sent" className="p-8 text-center flex-1 flex flex-col justify-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 border-2 border-emerald-500/50 flex items-center justify-center mb-4 text-emerald-400">
              <Send size={22}/>
            </div>
            <h3 className="font-display text-2xl font-bold text-white">Inquiry sent</h3>
            <p className="mt-2 text-sm text-zinc-300 leading-relaxed">
              Team NextScene will connect the talent with you within 48 hours. We'll email you at <span className="text-[#DC2626]">{form.contact_email}</span>.
            </p>
            <Button data-testid="inquiry-close" onClick={onClose} className="mt-6 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold h-11 mx-auto px-8">Done</Button>
          </div>
        ) : (
          <>
            <div className="overflow-y-auto p-4 space-y-3 flex-1">
              <p className="text-xs text-zinc-400 leading-relaxed">
                Fill this out and NextScene will route your inquiry to the talent and follow up over email/WhatsApp. Nothing is public.
              </p>

              <F label="Role you're casting for *">
                <Input data-testid="inq-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="e.g. Lead actor, Playback singer" className="bg-zinc-900 border-zinc-800"/>
              </F>
              <F label="Project / production name">
                <Input data-testid="inq-project" value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} placeholder="e.g. Bhojpuri web series" className="bg-zinc-900 border-zinc-800"/>
              </F>
              <F label="When do you need them?">
                <Input data-testid="inq-when" value={form.when_needed} onChange={(e) => setForm({ ...form, when_needed: e.target.value })} placeholder="e.g. Shoot in April 2026, 15 days" className="bg-zinc-900 border-zinc-800"/>
              </F>
              <F label="Requirements *">
                <Textarea data-testid="inq-req" value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} placeholder="Age, gender, look, skills, language, budget, location…" className="bg-zinc-900 border-zinc-800 min-h-[80px]"/>
              </F>

              <div className="pt-2 border-t border-zinc-900 mt-4">
                <p className="text-xs uppercase tracking-wider text-zinc-400 mb-3">Your contact</p>
                <div className="space-y-3">
                  <F label="Your name *"><Input data-testid="inq-name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="bg-zinc-900 border-zinc-800"/></F>
                  <F label="Company / studio (optional)"><Input data-testid="inq-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-zinc-900 border-zinc-800"/></F>
                  <F label="Email *"><Input data-testid="inq-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="bg-zinc-900 border-zinc-800"/></F>
                  <F label="Phone / WhatsApp"><Input data-testid="inq-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+91 98xxxxxxxx" className="bg-zinc-900 border-zinc-800"/></F>
                  <F label="Anything else?">
                    <Textarea data-testid="inq-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Optional note to the talent…" className="bg-zinc-900 border-zinc-800 min-h-[60px]"/>
                  </F>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-zinc-900 sticky bottom-0 bg-[#0A0605]">
              <Button data-testid="inq-submit" disabled={busy} onClick={submit} className="w-full h-12 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold">
                {busy ? <><Loader2 className="animate-spin mr-2" size={16}/> Sending…</> : <><Send size={16} className="mr-2"/> Send inquiry</>}
              </Button>
              <p className="text-[11px] text-zinc-400 text-center mt-2">Delivered to NextScene team + the talent. Verified inquiries only.</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-zinc-200 text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
